<template>
  <div class="filters-container" :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'">
    <v-expansion-panels class="filters-panel" flat>
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-icon>mdi-filter-variant</v-icon>
          <span class="ml-2">{{ $t("advancedFilters") }}</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row class="filters-row" no-gutters align="start" :dir="$i18n.locale === 'he' ? 'ltr' : 'rtl'">
            <v-col
              class="clear-btn-col"
              style="flex: 0 0 140px; max-width: 140px; min-width: 120px"
              align-self="end"
            >
              <v-btn
                color="secondary"
                variant="outlined"
                class="clear-filters-btn"
                @click="clearAllFilters"
                size="small"
              >
                {{ $t("clearAllFilters") }}
              </v-btn>
            </v-col>
            <v-col
              class="filter-col"
              v-for="(filter, idx) in filterGroups"
              :key="filter.key"
            >
              <span class="filter-label">{{ filter.label }}</span>
              <v-text-field
                :model-value="filter.search"
                @update:model-value="updateFilterSearch(filter.key, $event)"
                :placeholder="filter.placeholder"
                density="compact"
                variant="outlined"
                hide-details
                class="filter-search"
                prepend-inner-icon="mdi-magnify"
              />
              <div
                class="chips-scroll-wrapper"
                :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'"
              >
                <div class="filter-chips-container">
                  <v-chip
                    v-for="option in filter.options"
                    :key="option.value || option"
                    :class="[
                      filter.chipClass,
                      {
                        selected: isSelected(
                          filter.key,
                          option.value || option
                        ),
                      },
                    ]"
                    color="primary"
                    variant="outlined"
                    size="small"
                    @click="toggleChip(filter.key, option.value || option)"
                  >
                    {{ option.title || option }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
export default {
  name: "AdvancedFilters",
  props: {
    filters: {
      type: Object,
      required: true,
    },
    statusOptions: Array,
    typeOptions: Array,
    severityOptions: Array,
    layerOptions: Array,
  },
  emits: ["update:filters", "clear-filters"],
  data() {
    return {
      statusSearch: "",
      typeSearch: "",
      severitySearch: "",
      layerSearch: "",
    };
  },
  computed: {
    filterGroups() {
      return [
        this.buildFilterGroup(
          "status",
          this.statusOptions,
          this.statusSearch,
          this.$t("statusHeader"),
          this.$t("searchStatus"),
          "filter-chip"
        ),
        this.buildFilterGroup(
          "type",
          this.typeOptions,
          this.typeSearch,
          this.$t("typeHeader"),
          this.$t("searchType"),
          "filter-chip"
        ),
        this.buildFilterGroup(
          "severity",
          this.severityOptions,
          this.severitySearch,
          this.$t("severityHeader"),
          this.$t("searchSeverity"),
          "filter-chip"
        ),
        this.buildFilterGroup(
          "layer",
          this.layerOptions,
          this.layerSearch,
          this.$t("layerHeader"),
          this.$t("searchLayer"),
          "filter-chip"
        ),
      ];
    },
  },
  methods: {
    clearAllFilters() {
      this.$emit("clear-filters");
    },
    isSelected(key, value) {
      return this.filters[key] === value;
    },
    toggleChip(key, value) {
      const newFilters = { ...this.filters };
      newFilters[key] = newFilters[key] === value ? null : value;

      this.$emit("update:filters", newFilters);
    },
    updateFilterSearch(key, value) {
      if (key === 'status') this.statusSearch = value;
      else if (key === 'type') this.typeSearch = value;
      else if (key === 'severity') this.severitySearch = value;
      else if (key === 'layer') this.layerSearch = value;
    },
    buildFilterGroup(
      key,
      options,
      search,
      label,
      placeholder,
      chipClass,
    ) {
      const normalizedOptions = options?.map((opt) =>
        typeof opt === "object" ? opt : { value: opt, title: opt }
      );

      let filtered;
      if (!search) {
        filtered = normalizedOptions;
      } else {
        const selectedValues = this.getSelectedValues(key);
        const selectedOptions = normalizedOptions.filter((opt) =>
          selectedValues.includes(opt.value)
        );
        const searchFiltered = normalizedOptions.filter((opt) =>
          (opt.title || "").toLowerCase().includes(search.toLowerCase())
        );
        // Union by value
        filtered = [
          ...new Map(
            [...selectedOptions, ...searchFiltered].map((item) => [
              item.value,
              item,
            ])
          ).values(),
        ];
      }

      return {
        key,
        label,
        search,
        placeholder,
        options: filtered,
        chipClass,
      };
    },
    getSelectedValues(key) {
      return this.filters[key] ? [this.filters[key]] : [];
    },
  },
};
</script>

<style scoped>
.filters-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.filters-panel {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.filters-row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 1em;
}
.clear-btn-col, .filter-col {
  width: 20%;
  max-width: 20%;
  min-width: 120px;
  box-sizing: border-box;
}
.clear-filters-btn {
  width: 100%;
}
.filter-col {
  padding: 0.5em 0.2em;
}
.filter-label {
  font-size: 1em;
}

.filter-chip, .keyword-chip {
  margin: 2% !important;
}

.filter-chip.selected, .keyword-chip.selected {
  background-color: var(--v-theme-primary) !important;
  color: green !important;
  border-color: var(--v-theme-primary) !important;
}

@media (max-width: 900px) {
  .clear-btn-col, .filter-col {
    width: 33%;
    max-width: 33%;
    min-width: 100px;
  }
}
@media (max-width: 768px) {
  .filters-container, .filters-panel, .filters-row {
    width: 100% !important;
    max-width: 100% !important;
    flex-direction: column !important;
    gap: 0.5em;
    padding: 0.5em 0.2em;
  }
  .clear-btn-col, .filter-col {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    margin-bottom: 0.5em;
    padding: 0.3em 0.1em;
  }
  .clear-filters-btn {
    width: 100%;
  }
  .filter-label {
    font-size: 0.95em;
  }
}
</style>
